// Shared by the common `sentryBeforeSend` (web, apollo, nutrition) and Pulse's
// own client `beforeSend`. Kept import-free so Pulse can use it without pulling
// in the rest of error-filter.ts.

/**
 * Firebase SDK codes whose rejections the SDK itself leaves unobserved, so they
 * reach the global unhandledrejection handler with no public promise for us to
 * attach a handler to.
 *
 * `remoteconfig/storage-open`: Remote Config's component factory starts an
 * IndexedDB read-ahead at `getRemoteConfig()` time and drops the promise. On a
 * browser profile whose IndexedDB won't open (Chrome's "Internal error"), that
 * read-ahead fans out four reads on the one rejected open and awaits only the
 * first. LADDER-WEBSITE-8JM, 127 events / 23 users on /app.
 *
 * `remoteconfig/storage-get`: the same four reads when the database opens but
 * its reads then fail (WebKit deleting site data mid-session, an aborted
 * transaction). LADDER-WEBSITE-8RT / 8PE / 8XH, LADDER-PULSE-N5.
 *
 * `installations/app-offline`: while `navigator.onLine` is false, Installations
 * creates its registration rejection inside an IndexedDB `update()` callback and
 * only attaches `getId`'s `.catch` after the transaction commits — a task
 * boundary later, by which point the browser has already reported it. Analytics
 * init (`getAnalytics()` in `packages/ui/firebase.js`) is the caller.
 * LADDER-WEBSITE-8M2, 62 events / 19 users on /app.
 *
 * Keyed on the mechanism as well as the code, because the same conditions make
 * every first-party `fetchAndActivate` throw the identical error. Those throws
 * are caught by our callers — react-query on web, an explicit `reportError`
 * capture in apollo's remote-config editors — and the apollo capture is a real
 * admin-facing failure that must keep reporting. Only the SDK's unobserved
 * rejections match; a handled capture does not.
 */
const FIREBASE_UNOBSERVED_REJECTION_CODES =
  /remoteconfig\/storage-(?:open|get)|installations\/app-offline/i;

export function isFirebaseUnobservedSdkRejection(event: any): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  if (!FIREBASE_UNOBSERVED_REJECTION_CODES.test(exception.value ?? "")) {
    return false;
  }
  const mechanism = exception.mechanism;
  return (
    mechanism?.handled === false ||
    mechanism?.type === "onunhandledrejection"
  );
}
